import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { skipToken } from '@reduxjs/toolkit/query'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store'
import { useGetUserByIdQuery } from 'store/auth/auth.api'
import {
  useGetCompanyByIdQuery,
  useGetCompanyEmailsQuery,
  useGetUserCompanyAssociationByUserQuery,
} from 'store/company/company.api'
import {
  clearInvoiceEmail,
  setInvoiceEmailAttachments,
  setInvoiceEmailMessage,
  setInvoiceEmailSubject,
} from 'store/invoices/invoices.slice'
import {
  BatchResponse,
  DocumentType,
  InvoiceResponse,
  ListDocumentsResponse,
} from 'store/invoices/invoices.types'
import { isCompanyFeatureEnabled } from 'utils/features'
import { documentFullName } from '../invoice-details/invoice-table/invoice-table-body/invoice-table-documents/invoice-documents-dialog/invoice-documents-table/invoice-documents-table.utils'
import {
  useLazyListInvoicedocumentsQuery,
  useSendBatchInvoiceEmailMutation,
} from 'store/invoices/invoices.api'
import { DateTime } from 'luxon'
import InvoiceFromEmail from '../invoice-details/invoice-table/invoice-table-body/invoice-table-email-dialog/invoice-from-email/invoice-from-email.component'
import InvoiceToEmail from '../invoice-details/invoice-table/invoice-table-body/invoice-table-email-dialog/invoice-to-email/invoice-to-email.component'
import InvoiceEmailButtons from '../invoice-details/invoice-table/invoice-table-body/invoice-table-email-dialog/invoice-email-buttons/invoice-email-buttons.component'
import TranspotsLoading from 'components/transpots-loading/transpots-loading.component'
import TranspotsErrorSnackbar from 'components/transpots-error-snackbar/transpots-error-snackbar.component'
import InvoiceDocument from '../invoice-details/invoice-table/invoice-table-body/invoice-table-documents/invoice-document/invoice-document.component'
import {
  DocumentTypeNonDropdown,
  StyledTableCell,
  StyledTableRow,
} from '../invoice-details/invoice-table/invoice-table-body/invoice-table-documents/invoice-documents-dialog/invoice-documents-table/invoice-documents-table.styles'

type OrderDocumentItem = {
  docType: DocumentType
  documentName: string
  s3Url: string
}

type OrderDocumentGroup = {
  orderKey: string
  items: OrderDocumentItem[]
}

const BATCH_ORDER_DOC_TYPES: DocumentType[] = ['INVOICE', 'WAYBILL', 'POD', 'BOL']

type BatchInvoiceEmailDialogProps = {
  open: boolean
  batch: BatchResponse
  selectedInvoices: InvoiceResponse[]
  handleClose: () => void
}

const BatchInvoiceEmailDialog = ({
  open,
  batch,
  selectedInvoices,
  handleClose,
}: BatchInvoiceEmailDialogProps) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const dispatch = useDispatch()
  const invoices = useSelector((state: RootState) => state.invoices)
  const featuresByCompany = useSelector(
    (state: RootState) => state.auth.featuresByCompany
  )
  const [errorOnSending, setErrorOnSending] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [orderDocumentGroups, setOrderDocumentGroups] = useState<OrderDocumentGroup[]>([])
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set())
  const [sendBatchInvoiceEmail, { isLoading }] = useSendBatchInvoiceEmailMutation()
  const [fetchInvoiceDocuments] = useLazyListInvoicedocumentsQuery()

  const firstInvoice = selectedInvoices[0]
  const orderIdsText = selectedInvoices.map((invoice) => invoice.userOrderId).join(', ')
  const latestDueDate = useMemo(() => {
    if (selectedInvoices.length === 0) {
      return DateTime.now()
    }
    return selectedInvoices.reduce((latest, invoice) => {
      const current = DateTime.fromISO(invoice.dueDate)
      return current > latest ? current : latest
    }, DateTime.fromISO(selectedInvoices[0].dueDate))
  }, [selectedInvoices])

  const { data: company } = useGetCompanyByIdQuery(
    firstInvoice?.customerId ? { id: firstInvoice.customerId } : skipToken
  )
  const { data: companyEmails } = useGetCompanyEmailsQuery(
    firstInvoice?.customerId ? { companyId: firstInvoice.customerId } : skipToken
  )
  const { data: user } = useGetUserByIdQuery(
    firstInvoice?.userId ? { userId: firstInvoice.userId } : skipToken
  )
  const { data: userCompanyAssociations } = useGetUserCompanyAssociationByUserQuery(
    firstInvoice?.userId ? { userId: firstInvoice.userId } : skipToken
  )

  const toEmail = useMemo(
    () => {
      const latestInvoiceEmailFromTable = companyEmails?.find(
        (email) => email.emailType === 'InvoicePodEmail'
      )?.email
      return (
        latestInvoiceEmailFromTable ??
        company?.invoicePodEmail ??
        company?.accountPayableEmail ??
        company?.notificationEmail
      )
    },
    [
      companyEmails,
      company?.invoicePodEmail,
      company?.accountPayableEmail,
      company?.notificationEmail,
    ]
  )

  const userEmail = useMemo(() => {
    if (user?.email && user.email.trim() !== '') {
      return user.email.trim()
    }
    return null
  }, [user?.email])

  const showWaybill = useMemo(
    () =>
      !firstInvoice?.customerId ||
      isCompanyFeatureEnabled(
        featuresByCompany,
        firstInvoice.customerId,
        'WAYBILL'
      ),
    [featuresByCompany, firstInvoice?.customerId]
  )

  const factoringEmails = useMemo(() => {
    if (!firstInvoice?.userId || !firstInvoice?.customerId || !userCompanyAssociations) {
      return []
    }
    const association = userCompanyAssociations.find(
      (assoc) =>
        assoc.companyId === firstInvoice.customerId && assoc.factoringEnabled
    )
    if (association) {
      return ['accounting@transpots.com']
    }
    return []
  }, [firstInvoice?.userId, firstInvoice?.customerId, userCompanyAssociations])

  const senderName = useMemo(() => {
    const companyName = user?.companyName?.trim()
    if (companyName) {
      return companyName
    }
    return user?.email?.trim() ?? ''
  }, [user?.companyName, user?.email])

  const batchSubject = useMemo(() => {
    return `Batch Invoice ${batch.batchId} from ${senderName}. Orders: ${orderIdsText}`
  }, [batch.batchId, orderIdsText, senderName])

  const batchMessage = useMemo(() => {
    return `Your batch invoice amount is $${batch.totalAmount.toFixed(2)} for orders ${orderIdsText}. This batch invoice is due on ${latestDueDate.toFormat(
      'LLL dd, yyyy'
    )}`
  }, [batch.totalAmount, latestDueDate, orderIdsText])

  const buildOrderDocumentGroup = useCallback(
    (invoice: InvoiceResponse, documents: ListDocumentsResponse): OrderDocumentGroup => {
      const docTypes = showWaybill
        ? BATCH_ORDER_DOC_TYPES
        : BATCH_ORDER_DOC_TYPES.filter((type) => type !== 'WAYBILL')

      const items = docTypes.flatMap((docType) =>
        (documents[docType] ?? []).map((file) => ({
          docType,
          documentName: file.documentName || file.originalName,
          s3Url: file.s3Path,
        }))
      )

      return {
        orderKey: invoice.userOrderId || invoice.orderId,
        items,
      }
    },
    [showWaybill]
  )

  const attachmentS3Urls = useMemo(() => {
    const urls: string[] = []
    if (batch.batchS3Url) {
      urls.push(batch.batchS3Url)
    }
    orderDocumentGroups.forEach((group) => {
      group.items.forEach((item) => urls.push(item.s3Url))
    })
    return urls
  }, [batch.batchS3Url, orderDocumentGroups])

  useEffect(() => {
    const fetchOrderDocuments = async () => {
      if (!open || selectedInvoices.length === 0) return
      try {
        const groups: OrderDocumentGroup[] = []
        for (const invoice of selectedInvoices) {
          const docs = await fetchInvoiceDocuments({ orderId: invoice.orderId }).unwrap()
          groups.push(buildOrderDocumentGroup(invoice, docs))
        }
        setOrderDocumentGroups(groups)
        setExpandedOrders(new Set())
      } catch {
        setErrorOnSending(true)
        setErrorMessage('Failed to load order documents for selected batch orders.')
      }
    }
    fetchOrderDocuments()
  }, [open, selectedInvoices, fetchInvoiceDocuments, buildOrderDocumentGroup])

  useEffect(() => {
    if (!open) return
    dispatch(setInvoiceEmailSubject(batchSubject))
    dispatch(setInvoiceEmailMessage(batchMessage))
    dispatch(setInvoiceEmailAttachments(attachmentS3Urls))
  }, [open, dispatch, batchSubject, batchMessage, attachmentS3Urls])

  const toggleOrderExpanded = (orderKey: string) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev)
      if (next.has(orderKey)) {
        next.delete(orderKey)
      } else {
        next.add(orderKey)
      }
      return next
    })
  }

  const handleSendBatchInvoice = async () => {
    if (selectedInvoices.length === 0 || invoices.invoiceEmailAttachments.length === 0) {
      setErrorOnSending(true)
      setErrorMessage('No invoices or attachments available for batch email.')
      return
    }
    try {
      await sendBatchInvoiceEmail({
        invoiceIds: selectedInvoices.map((invoice) => invoice.id),
        recipient: invoices.invoiceEmailTo,
        subject: invoices.invoiceEmailSubject,
        message: invoices.invoiceEmailMessage,
        s3Urls: invoices.invoiceEmailAttachments,
      }).unwrap()
      handleClose()
      dispatch(clearInvoiceEmail())
    } catch (error: any) {
      setErrorOnSending(true)
      setErrorMessage(error?.error || 'Failed to send batch invoice email.')
    }
  }

  const batchFileName = `batch-invoice-${batch.batchId}.pdf`

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <Box>
        <Typography sx={{ bgcolor: 'secondary.light', p: 2 }}>
          Send by Email / {batch.batchId}
        </Typography>
        <List>
          <InvoiceFromEmail />
          <InvoiceToEmail
            defaultEmail={toEmail}
            errorOnSending={errorOnSending}
            userEmail={userEmail}
            factoringEmails={factoringEmails}
          />
          <ListItem>
            <ListItemText>Subject: {batchSubject}</ListItemText>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText>Message: {batchMessage}</ListItemText>
          </ListItem>
          <Divider />
        </List>

        <TableContainer component={Paper} sx={{ borderRadius: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell theme={theme}>Documents Type</StyledTableCell>
                <StyledTableCell theme={theme}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography>Documents</Typography>
                    <Typography>Actions</Typography>
                  </Stack>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batch.batchS3Url && (
                <StyledTableRow theme={theme}>
                  <StyledTableCell theme={theme}>
                    <DocumentTypeNonDropdown
                      theme={theme}
                      isupload="false"
                      condensed="true"
                    >
                      Batch
                    </DocumentTypeNonDropdown>
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row" theme={theme}>
                    <Stack
                      my={2}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <InvoiceDocument
                        document={{
                          documentName: batchFileName,
                          originalName: batchFileName,
                          s3Path: batch.batchS3Url,
                        }}
                      />
                    </Stack>
                  </StyledTableCell>
                </StyledTableRow>
              )}

              {orderDocumentGroups.map((group) => {
                const isExpanded = expandedOrders.has(group.orderKey)
                return (
                  <Fragment key={group.orderKey}>
                    <TableRow
                      onClick={() => toggleOrderExpanded(group.orderKey)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <TableCell colSpan={2} sx={{ py: 0.75, borderBottom: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.25}>
                          <IconButton
                            size="small"
                            sx={{ p: 0.25 }}
                            aria-label={isExpanded ? 'Collapse order' : 'Expand order'}
                            onClick={(event) => {
                              event.stopPropagation()
                              toggleOrderExpanded(group.orderKey)
                            }}
                          >
                            {isExpanded ? (
                              <KeyboardArrowDownIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowRightIcon fontSize="small" />
                            )}
                          </IconButton>
                          <Typography
                            component="span"
                            variant="subtitle2"
                            fontWeight={700}
                            color="text.primary"
                          >
                            {group.orderKey}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {isExpanded && group.items.length === 0 && (
                      <StyledTableRow
                        key={`order-empty-${group.orderKey}`}
                        theme={theme}
                      >
                        <StyledTableCell colSpan={2} theme={theme}>
                          <Typography variant="body2" color="text.secondary">
                            No documents available for this order.
                          </Typography>
                        </StyledTableCell>
                      </StyledTableRow>
                    )}
                    {isExpanded &&
                      group.items.map((item) => (
                        <StyledTableRow
                          key={`${group.orderKey}-${item.docType}-${item.s3Url}`}
                          theme={theme}
                        >
                          <StyledTableCell theme={theme}>
                            <DocumentTypeNonDropdown
                              theme={theme}
                              isupload="false"
                              condensed="true"
                            >
                              {`${group.orderKey} – ${documentFullName(item.docType)}`}
                            </DocumentTypeNonDropdown>
                          </StyledTableCell>
                          <StyledTableCell component="th" scope="row" theme={theme}>
                            <Stack
                              my={2}
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <InvoiceDocument
                                document={{
                                  documentName: item.documentName,
                                  originalName: item.documentName,
                                  s3Path: item.s3Url,
                                }}
                              />
                            </Stack>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                  </Fragment>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <InvoiceEmailButtons
          handleClose={handleClose}
          handleSendInvoice={handleSendBatchInvoice}
        />
      </Box>
      {isLoading && <TranspotsLoading loadingText="Sending Email..." />}
      <TranspotsErrorSnackbar
        message={errorMessage}
        open={errorOnSending}
        handleClose={() => setErrorOnSending(false)}
      />
    </Dialog>
  )
}

export default BatchInvoiceEmailDialog
